import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';

/**
 * Where a visitor stands on the way into Desk. Every step is a fact the server
 * would enforce anyway; this only decides which screen explains the next step.
 *
 *   signed-out    no session
 *   not-staff     signed in, but not on Desk (and someone already owns it)
 *   ready         staff: RLS now returns rows
 *
 * Email and password is the whole sign-in (20261023120000_desk_password_only.sql).
 */
export type Gate =
  | { step: 'loading' }
  | { step: 'signed-out' }
  | { step: 'not-staff'; session: Session }
  | { step: 'ready'; session: Session };

interface AuthApi {
  gate: Gate;
  /** Re-derive the gate after signing in. */
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthApi | null>(null);

async function deriveGate(session: Session | null): Promise<Gate> {
  if (!session) return { step: 'signed-out' };

  // The first account to sign in becomes the owner; for everyone after that
  // this simply reports whether they are staff. See the migration
  // 20261013120000_desk_staff.sql.
  const { data: isStaff, error } = await supabase.rpc('claim_first_owner');
  if (error) throw error;
  return isStaff ? { step: 'ready', session } : { step: 'not-staff', session };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [gate, setGate] = useState<Gate>({ step: 'loading' });

  const settle = useCallback(async (session: Session | null) => {
    try {
      setGate(await deriveGate(session));
    } catch (err) {
      console.error('Desk: could not establish the sign-in state', err);
      // A session the server rejects is no session.
      await supabase.auth.signOut({ scope: 'local' });
      setGate({ step: 'signed-out' });
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => settle(data.session));
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      // Token refreshes change nothing; only real transitions re-gate.
      // Deferred a tick: awaiting another auth call inside this callback
      // deadlocks supabase-js (it holds its auth lock while notifying).
      if (event === 'SIGNED_OUT') setGate({ step: 'signed-out' });
      if (event === 'USER_UPDATED') {
        setTimeout(() => settle(session), 0);
      }
    });
    return () => data.subscription.unsubscribe();
  }, [settle]);

  const api = useMemo<AuthApi>(
    () => ({
      gate,
      refresh: async () => {
        const { data } = await supabase.auth.getSession();
        await settle(data.session);
      },
      signOut: async () => {
        await supabase.auth.signOut();
        setGate({ step: 'signed-out' });
      },
    }),
    [gate, settle],
  );

  return <AuthContext.Provider value={api}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

/** The signed-in staff session. Only valid inside the ready shell. */
export function useStaffSession() {
  const { gate } = useAuth();
  if (gate.step !== 'ready') throw new Error('useStaffSession outside the ready shell');
  return gate.session;
}
